// Shown after sign-in when the merge function detected real divergence —
// both this device and the cloud have non-trivial chat history / relationships
// / profile / mode and they disagree. Saved words and characters are always
// merged (additive), so this prompt is only about the newest-wins fields.

import { Cloud, Smartphone } from 'lucide-react';
import type { AppSnapshot } from '@/lib/storage/repository';
import { C } from '@/styles/tokens';

interface ConflictPromptProps {
  local: AppSnapshot;
  server: AppSnapshot;
  onChoose: (side: 'local' | 'server') => void;
}

interface SideSummary {
  chats: number;
  matches: number;
  custom: number;
  words: number;
  streak: number;
  lastSaved: string;
}

function summarize(s: AppSnapshot): SideSummary {
  const chats = Object.values(s.chatStates).filter((c) => c.history.length > 1).length;
  const matches = Object.values(s.relationships).filter((r) => r.matched).length;
  return {
    chats,
    matches,
    custom: s.customPersonas.length,
    words: s.library.length,
    streak: s.profile.streak,
    lastSaved: s.savedAt,
  };
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!t || Number.isNaN(t)) return 'never';
  const diffMs = Date.now() - t;
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

export function ConflictPrompt({ local, server, onChoose }: ConflictPromptProps) {
  const ls = summarize(local);
  const ss = summarize(server);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label="Resolve sync conflict"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
    >
      <div className="absolute inset-0 anim-backdrop-in" style={{ background: 'rgba(26,22,20,0.55)' }} aria-hidden />
      <div
        className="relative w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] anim-sheet-up overflow-hidden"
        style={{ background: C.cream, boxShadow: '0 -10px 30px rgba(26,22,20,0.22)' }}
      >
        <div className="px-6 pt-6 pb-4">
          <div className="ff-body text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: C.vermillionDeep }}>
            Sync conflict
          </div>
          <h2 className="ff-display italic text-2xl mb-2" style={{ color: C.ink }}>
            We found data on both sides
          </h2>
          <p className="ff-body text-[13px] leading-relaxed" style={{ color: C.inkLight }}>
            Saved words and custom characters will be combined either way. Pick which side&apos;s
            <strong> chats, matches, and progress</strong> to keep — the other will be replaced.
          </p>
        </div>

        <div className="px-4 pb-2 grid grid-cols-2 gap-3">
          <SideCard
            icon={<Cloud size={16} aria-hidden />}
            title="Cloud"
            tagline={`Last saved ${formatRelative(ss.lastSaved)}`}
            summary={ss}
            onPick={() => onChoose('server')}
          />
          <SideCard
            icon={<Smartphone size={16} aria-hidden />}
            title="This device"
            tagline={`Last saved ${formatRelative(ls.lastSaved)}`}
            summary={ls}
            onPick={() => onChoose('local')}
          />
        </div>

        <div className="px-6 pb-6 pt-3">
          <p className="ff-body text-[10px] text-center" style={{ color: C.inkSofter }}>
            You can&apos;t undo this. Library and characters merge regardless.
          </p>
        </div>
      </div>
    </div>
  );
}

interface SideCardProps {
  icon: React.ReactNode;
  title: string;
  tagline: string;
  summary: SideSummary;
  onPick: () => void;
}

function SideCard({ icon, title, tagline, summary, onPick }: SideCardProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="text-left p-4 rounded-2xl transition-transform active:scale-[0.98]"
      style={{
        background: 'white',
        border: `1px solid ${C.blush}`,
        boxShadow: '0 2px 8px rgba(26,22,20,0.06)',
      }}
    >
      <div className="flex items-center gap-2 mb-2" style={{ color: C.vermillion }}>
        {icon}
        <span className="ff-body text-[11px] uppercase tracking-[0.2em] font-bold">{title}</span>
      </div>
      <div className="ff-body text-[10px] mb-3" style={{ color: C.inkSofter }}>
        {tagline}
      </div>
      <ul className="space-y-1.5">
        <Stat label="chats" value={summary.chats} />
        <Stat label="matches" value={summary.matches} />
        <Stat label="custom characters" value={summary.custom} />
        <Stat label="streak" value={`${summary.streak}d`} />
      </ul>
      <div
        className="mt-3 py-2 rounded-full ff-body text-[11px] font-bold uppercase tracking-[0.18em] text-center text-white"
        style={{ background: C.ink }}
      >
        Keep {title.toLowerCase()}
      </div>
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <li className="flex justify-between ff-body text-[12px]">
      <span style={{ color: C.inkLight }}>{label}</span>
      <span className="font-bold" style={{ color: C.ink }}>
        {value}
      </span>
    </li>
  );
}
